import "./auto-dispatch-grid.css";

import type { CompositeFilterDescriptor } from "@tql/ui-components-react/kendo/data-query";
import { useAtom } from "jotai";
import { useEffect, useMemo, useState, useTransition } from "react";

import type { column } from "~/features/auto-dispatch/hooks/use-get-columns.tsx";
import { accessorialsFilterIdsAtom } from "~/features/auto-dispatch/state/accessorials-filter-atom.ts";
import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";
import type {
	CustomerCarrier,
	GlobalCarrier,
} from "~/shared/schemas/auto-dispatch-schema";

type AutoDispatchGridProps = {
	columns: column[];
	data: (GlobalCarrier | CustomerCarrier)[];
	isLoading?: boolean;
};

export const AutoDispatchGrid = ({
	columns,
	data,
	isLoading,
}: AutoDispatchGridProps) => {
	// `committedFilter` is the only filter state fed to the grid. It is
	// updated inside a transition so the heavy mount/unmount of rows +
	// custom cells (TimePicker, DropDownList, MultiSelect) doesn't block
	// the UI; while the transition is pending, `isFilterPending` is true
	// and we show the skeleton loader for immediate visual feedback.
	//
	// We intentionally do NOT keep a separate "live" filter state — the
	// underlying Kendo Grid has `autoProcessData` enabled, which re-applies
	// whatever filter we pass it on every render. Feeding it a live filter
	// in addition to our own filtering produced a duplicate, immediate
	// filtering path that defeated the transition.
	const [committedFilter, setCommittedFilter] = useState<
		CompositeFilterDescriptor | undefined
	>();
	const [isFilterPending, startFilterTransition] = useTransition();

	// Accessorials selection lives in a Jotai atom rather than Kendo's
	// composite filter descriptor — Kendo's `filterBy` can't compare against
	// an array-of-objects field, and any descriptor we feed it for this
	// column gets re-applied internally and wipes the grid.
	const [accessorialIds, setAccessorialIds] = useAtom(
		accessorialsFilterIdsAtom,
	);
	const [committedAccessorialIds, setCommittedAccessorialIds] = useState<
		number[]
	>([]);

	// Mirror accessorial-id selection changes through a transition as well.
	useEffect(() => {
		startFilterTransition(() => {
			setCommittedAccessorialIds(accessorialIds);
		});
	}, [accessorialIds]);

	// Clear the global atom when this grid unmounts so the selection doesn't
	// leak across page navigations.
	useEffect(() => {
		return () => setAccessorialIds([]);
	}, [setAccessorialIds]);

	const handleFilterChange = (next: CompositeFilterDescriptor | undefined) => {
		startFilterTransition(() => {
			setCommittedFilter(next);
		});
	};

	// Only the accessorial-id post-filter runs here; the Kendo filter
	// descriptor is applied by the Grid's `autoProcessData`.
	const filteredData = useMemo(() => {
		if (committedAccessorialIds.length === 0) return data;
		return data.filter((row) => {
			const accessorials = (row as GlobalCarrier).accessorials;
			if (!accessorials || accessorials.length === 0) return false;
			return committedAccessorialIds.every((id) =>
				accessorials.some((a) => a.id === id),
			);
		});
	}, [data, committedAccessorialIds]);

	return (
		<div className={"auto-dispatch-grid-fill"}>
			<TraxGrid
				className={"auto-dispatch-grid"}
				columns={columns}
				data={filteredData}
				dataItemKey={"carrierId"}
				defaultTake={Infinity}
				filter={committedFilter}
				filterable={true}
				loading={isLoading === true || isFilterPending}
				onFilterChange={(e) => handleFilterChange(e.filter)}
				pageable={false}
				scrollable={"scrollable"}
				sortable={true}
				style={{ height: "100%" }}
			/>
		</div>
	);
};
